/**
 * appLoaderStore — the multi-flag gate behind the boot-time FullscreenLoader.
 *
 * Each subsystem that blocks the app from being usable (auth resolution, lazy
 * route chunk downloads, future workspace/branding fetches) raises a named
 * flag while it's in flight and lowers it when done. The loader stays up as
 * long as ANY flag is raised.
 *
 * The whole point is stability of the loader's React element. AppLoaderGate
 * renders ONE ``<FullscreenLoader />`` at the very top of the tree, so its
 * <video> DOM node lives across the entire boot sequence — the video does not
 * restart from frame 0 when auth resolves and Suspense takes over. Contrast
 * with the previous shape where AuthContext and AppRoutes each mounted their
 * own FullscreenLoader at different tree positions, causing a visible restart
 * on the handoff.
 *
 * Contract for callers: always raise/lower from inside a component's
 * ``useEffect`` (with the matching lower in the cleanup) so mount lifecycle
 * bounds the flag. A raise() outside a mount effect risks a leaked flag that
 * strands the loader on screen forever.
 */
import { create } from 'zustand';

interface AppLoaderState {
  flags: Set<string>;
  raise: (key: string) => void;
  lower: (key: string) => void;
}

// Initial state has 'auth' raised — the app always boots into an auth check
// (AuthContext lowers it once loadUserProfile resolves). Starting empty would
// leave a one-frame window where AppLoaderGate paints no loader before
// AuthContext's mount effect runs.
export const useAppLoaderStore = create<AppLoaderState>((set) => ({
  flags: new Set(['auth']),
  raise: (key) =>
    set((s) => {
      if (s.flags.has(key)) return s;
      const next = new Set(s.flags);
      next.add(key);
      return { flags: next };
    }),
  lower: (key) =>
    set((s) => {
      if (!s.flags.has(key)) return s;
      const next = new Set(s.flags);
      next.delete(key);
      return { flags: next };
    }),
}));

/** True while any subsystem has a boot-blocking flag raised. */
export const useIsAppLoading = () =>
  useAppLoaderStore((s) => s.flags.size > 0);
